import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { DateNTime } from "@/shared/utils/dateTime";

import { NotificationItem } from "../types/notification.type";

type NotificationListProps = {
  notifications: NotificationItem[];
  onNotificationOpen?: (notification: NotificationItem) => void;
};

export function NotificationList({ notifications, onNotificationOpen }: NotificationListProps) {
  const { t } = useTranslation("notification");

  if (notifications.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8, px: 2 }}>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
            fontWeight: 500,
            fontSize: "1.15rem",
            color: "var(--primary-800)",
            mb: 0.75,
          }}
        >
          {t("no_notifications_title")}
        </Typography>
        <Typography
          sx={{
            fontFamily: "var(--primary-font-family)",
            fontSize: "0.8125rem",
            color: "var(--primary-700)",
          }}
        >
          {t("no_notifications_subtitle")}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {notifications.map((item) => (
        <NotificationCard
          key={item.id}
          item={item}
          onClick={() => onNotificationOpen?.(item)}
        />
      ))}
    </Stack>
  );
}

function NotificationCard({
  item,
  onClick,
}: {
  item: NotificationItem;
  onClick: () => void;
}) {
  const { t } = useTranslation("notification");
  const isUnread = !item.is_read;
  const status = getStatusLabel(item.notification_type);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderRadius: 2.5,
        border: "1px solid var(--primary-300)",
        bgcolor: "var(--primary-100-light)",
        boxShadow: isUnread ? "inset 4px 0 0 0 var(--primary-600)" : "none",
        cursor: "pointer",
        "&:hover": {
          bgcolor: "var(--primary-100)",
        },
      }}
    >
      <Avatar
        sx={{
          width: 48,
          height: 48,
          bgcolor: "var(--primary-200)",
          color: "var(--primary-800)",
          fontWeight: 700,
          fontSize: "1rem",
          flexShrink: 0,
        }}
      >
        {(item.title || "?").charAt(0).toUpperCase()}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0, pr: isUnread ? 5 : 0 }}>
        <Typography
          sx={{
            fontFamily: "var(--primary-font-family)",
            fontWeight: 700,
            fontSize: "0.9375rem",
            color: "var(--primary-1000)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.title}
        </Typography>

        <Typography
          sx={{
            mt: 0.35,
            fontFamily: "var(--primary-font-family)",
            fontSize: "0.8125rem",
            color: "var(--primary-800)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.message}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75 }}>
          <Typography
            sx={{
              fontFamily: "var(--primary-font-family)",
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "var(--primary-700)",
            }}
          >
            {DateNTime.formatRelative(item.created_at)}
          </Typography>
          {status && (
            <Typography
              sx={{
                fontFamily: "var(--primary-font-family)",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "var(--secondary-700)",
              }}
            >
              • {status}
            </Typography>
          )}
        </Stack>
      </Box>

      {isUnread && (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            right: 40,
            px: 1,
            py: 0.35,
            borderRadius: "999px",
            bgcolor: "var(--primary-600)",
            color: "var(--primary-bg-dark)",
            fontFamily: "var(--primary-font-family)",
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          }}
        >
          {t("new_tag")}
        </Box>
      )}

      <ChevronRightRoundedIcon
        sx={{ fontSize: 22, color: "var(--primary-700)", flexShrink: 0 }}
      />
    </Box>
  );
}

function getStatusLabel(type: string) {
  const value = (type || "").toLowerCase();
  if (value.includes("purchased") || value.includes("purchase")) return "Purchased";
  if (value.includes("updated") || value.includes("update")) return "Updated";
  return null;
}
